import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { fetchUserQuestions } from "../../functions/fetchUserQuestions";
import { getHealthSprintStageContext } from "../../functions/healthSprintStageContext";
import { updateStage } from "../../functions/updateStage";
import { fetchStageProgress } from "../../functions/fetchStageProgress";
import { setBackButtonUrl } from "../uiSlice";
import {
  sendReport,
  extractReportIdFromResponse,
  normalizeReportTimeValue,
  resolveReportIdForSendReport,
  resolveReportsAuthToken,
} from "../../functions/sendReport";
import { mergeReportId } from "../loginSlice";
import {
  resolveThemeSoundUrl,
  resolveThemeDefaultQuestionSoundUrl,
  resolveQuestionMusicLoopUrl,
  hasAudioUrl,
} from "../../functions/healthSprintSounds";
import { attachAudioGestureOnce, resumeAudioContext } from "../../functions/healthSprintWebAudio";
import { useThemeIconUrls, ThemeIconImg } from "../../functions/healthSprintThemeIcons";
import { isFromMobileApp, navigateExternalUrl } from "../../functions/navigationHelper";
import "./healthsprint.css";

const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));

/** Case-insensitive compare so session context matches `fetchThemeData` themename. */
function themeNamesMatch(a, b) {
  return String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
}

/** Starting value for Energy / Time / Health meters (0–100; also matches new `stages` rows). */
const INITIAL_METERS = 50;

/** After picking an answer: show meter impact preview this long, then commit and (after flash) DYK. */
const CHOICE_COMMIT_DELAY_MS = 2000;

/** Floating +/- impact duration; Do You Know opens only after this (same timing as hiding the flash). */
const METER_FLASH_DURATION_MS = 2000;

const HUD_TOUR_STORAGE_PREFIX = "hs_hud_intro_tour_v2_";

function hudTourStorageKey(userId) {
  return `${HUD_TOUR_STORAGE_PREFIX}${userId != null && String(userId).trim() !== "" ? String(userId).trim() : "guest"}`;
}

/** Padding around each spotlight target (reads as a slight zoom). */
const HUD_TOUR_PAD = 12;
/**
 * Meter columns sit side-by-side; symmetric large padding makes the ring eat the neighbour column.
 * Use tight horizontal pads toward the adjacent column (see measureHudTourStep).
 */
const HUD_TOUR_PAD_METER_ICONS = { t: 12, r: 4, b: 12, l: 10 };
const HUD_TOUR_PAD_METER_BARS = { t: 12, r: 10, b: 12, l: 4 };
/** Circular controls (power-ups, sound): minimal pad so the ring matches the circle, not a big rounded square. */
const HUD_TOUR_PAD_CIRCLE = 6;

function resolveHudTourPad(spec) {
  const d = HUD_TOUR_PAD;
  if (typeof spec === "number") {
    return { top: spec, right: spec, bottom: spec, left: spec };
  }
  if (!spec || typeof spec !== "object") {
    return { top: d, right: d, bottom: d, left: d };
  }
  return {
    top: typeof spec.t === "number" ? spec.t : typeof spec.top === "number" ? spec.top : d,
    right: typeof spec.r === "number" ? spec.r : typeof spec.right === "number" ? spec.right : d,
    bottom: typeof spec.b === "number" ? spec.b : typeof spec.bottom === "number" ? spec.bottom : d,
    left: typeof spec.l === "number" ? spec.l : typeof spec.left === "number" ? spec.left : d,
  };
}

/** Keep focus ring + dimmer hole fully inside the viewport so corners are not clipped at screen edges. */
const HUD_TOUR_VIEWPORT_INSET = 10;

/** Vertical gap between spotlight hole and tooltip card (pointer sits in this gap). */
const HUD_TOUR_CARD_GAP = 18;

/** Bottom HUD controls sit off-screen on narrow viewports until we scroll them into view. */
const HUD_TOUR_MOBILE_SCROLL_STEP_IDS = new Set(["energyBarPowerup", "energyDrink", "sound"]);

function isHudTourBottomControlStep(id) {
  return id === "sound" || id === "energyBarPowerup" || id === "energyDrink";
}

function clampHudTourHoleToViewport(left, top, width, height, inset = HUD_TOUR_VIEWPORT_INSET) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;
  if (vw <= 0 || vh <= 0) return { left, top, width, height };
  let l = left;
  let t = top;
  let w = width;
  let h = height;
  if (l < inset) {
    const d = inset - l;
    l = inset;
    w -= d;
  }
  if (t < inset) {
    const d = inset - t;
    t = inset;
    h -= d;
  }
  if (l + w > vw - inset) {
    w = vw - inset - l;
  }
  if (t + h > vh - inset) {
    h = vh - inset - t;
  }
  return {
    left: l,
    top: t,
    width: Math.max(0, w),
    height: Math.max(0, h),
  };
}

const HUD_TOUR_STEP_COPY = {
  meterIconsGroup: {
    line1: "Energy, Time, and Health — each row has its own icon.",
    line2: "Lightning is Energy, the Clock is Time, and the Heart is Health.",
  },
  meterBarsGroup: {
    line1: "These three bars show your current levels at a glance.",
    line2: "They move when you preview answers or lock in a choice — stay balanced.",
  },
  energyBarPowerup: {
    line1: "Energy bar power-up — tap to use a charge.",
    line2: "+10 Energy and +5 Health. You only get a few per game.",
  },
  energyDrink: {
    line1: "Energy drink — a different power-up.",
    line2: "+10 Energy and −5 Time Management. Use it when it is worth the trade.",
  },
  sound: {
    line1: "Sound controls background music.",
    line2: "Mute or set volume; some effects may still play.",
  },
  dayCounter: {
    line1: "This is your current day in the sprint.",
    line2: "Answer the scenario to move forward when you are ready.",
  },
};

function hasSavedProgress(stage) {
  if (!stage) return false;
  let a = [];
  try {
    a = JSON.parse(stage.answers || "[]");
  } catch {
    return false;
  }
  if (Array.isArray(a) && a.length > 0) return true;
  const cur = parseInt(String(stage.current ?? ""), 10);
  if (Number.isFinite(cur) && cur > 0) return true;
  const e = stage.energy_score;
  const t = stage.time_score;
  const h = stage.health_score;
  if (e != null && t != null && h != null && (Number(e) !== INITIAL_METERS || Number(t) !== INITIAL_METERS || Number(h) !== INITIAL_METERS)) return true;
  const r = stage.questionsParsed?.resume;
  if (r && (r.screen === "dyk" || r.screen === "recovery")) return true;
  return false;
}

/**
 * HUD intro when the `stages` row from getStageProgress still has no real play data
 * (`points` / `total_score` from updateStage, plus answers / meters / resume in hasSavedProgress).
 */
function shouldOfferHudIntroTourFromStage(stage) {
  if (stage == null) return true;
  const points = Number(stage.points);
  const totalScore = Number(stage.total_score);
  if (Number.isFinite(points) && points > 0) return false;
  if (Number.isFinite(totalScore) && totalScore > 0) return false;
  if (hasSavedProgress(stage)) return false;
  return true;
}

/** Read DB/API fields with snake_case or camelCase keys. */
function qField(q, snake, camel) {
  const v = q[snake] ?? q[camel];
  return v;
}

function parseDaysFromQuestions(allQuestions) {
  const rows = Array.isArray(allQuestions)
    ? allQuestions
    : allQuestions?.allQuestions || allQuestions?.questions || [];

  const safeParseInt = (x) => {
    const n = typeof x === "number" ? x : parseInt(String(x ?? "").trim(), 10);
    return Number.isFinite(n) ? n : 0;
  };

  const popupTrim = (v) =>
    typeof v === "string" && v.trim().length > 0 ? v.trim() : "";

  return rows
    .map((q, idx) => {
      const rawChoices = [
        {
          label: qField(q, "option_one", "optionOne") || "",
          desc: "",
          energy: safeParseInt(qField(q, "option_one_impact_energy", "optionOneImpactEnergy")),
          time: safeParseInt(qField(q, "option_one_impact_time", "optionOneImpactTime")),
          health: safeParseInt(qField(q, "option_one_impact_health", "optionOneImpactHealth")),
          popup: popupTrim(qField(q, "option_one_popup", "optionOnePopup")),
        },
        {
          label: qField(q, "option_two", "optionTwo") || "",
          desc: "",
          energy: safeParseInt(qField(q, "option_two_impact_energy", "optionTwoImpactEnergy")),
          time: safeParseInt(qField(q, "option_two_impact_time", "optionTwoImpactTime")),
          health: safeParseInt(qField(q, "option_two_impact_health", "optionTwoImpactHealth")),
          popup: popupTrim(qField(q, "option_two_popup", "optionTwoPopup")),
        },
        {
          label: qField(q, "option_three", "optionThree") || "",
          desc: "",
          energy: safeParseInt(qField(q, "option_three_impact_energy", "optionThreeImpactEnergy")),
          time: safeParseInt(qField(q, "option_three_impact_time", "optionThreeImpactTime")),
          health: safeParseInt(qField(q, "option_three_impact_health", "optionThreeImpactHealth")),
          popup: popupTrim(qField(q, "option_three_popup", "optionThreePopup")),
        },
      ];
      const choices = rawChoices.filter((c) => (c.label || "").trim().length > 0);
      const dyk = rawChoices.map((c) => c.popup).filter(Boolean);

      return {
        title: qField(q, "question_title", "questionTitle") || `DAY ${idx + 1}`,
        scenario: qField(q, "question", "questionText") || "",
        choices,
        dyk,
        /** S3 filename or full URL — optional per-question background loop (see `resolveQuestionMusicLoopUrl`). */
        music: qField(q, "music", "musicFile") ?? "",
        music_url: qField(q, "music_url", "musicUrl") ?? "",
      };
    })
    .filter((d) => d.choices.length > 0);
}

/** "Do you know" copy for the option the user just picked (`answersSelected[day]` is 1-based index into `choices`). */
function getDykTextForSelectedChoice(dayData, answersSelected, dayIndex) {
  const pick = answersSelected?.[dayIndex];
  if (pick == null || pick < 1 || !dayData?.choices?.[pick - 1]) return "";
  const t = dayData.choices[pick - 1].popup;
  return typeof t === "string" && t.trim() ? t.trim() : "";
}

function useFlashMeter() {
  const [flash, setFlash] = useState(null);
  const tRef = useRef(null);

  const trigger = useCallback((delta) => {
    if (tRef.current) window.clearTimeout(tRef.current);
    setFlash(delta);
    tRef.current = window.setTimeout(() => setFlash(null), METER_FLASH_DURATION_MS);
  }, []);

  useEffect(() => () => tRef.current && window.clearTimeout(tRef.current), []);
  return { flash, trigger };
}

/**
 * CSS variables from themeData + themeData.colors (same shape as USER.jsx / themeset).
 * Priority: theme root (admin Health Sprint fields, button_color, …) → colors.* → fallback.
 */
function useHealthSprintThemeVars(themeData) {
  return useMemo(() => {
    const t = themeData || {};
    const c = t.colors && typeof t.colors === "object" && !Array.isArray(t.colors) ? t.colors : {};

    const root = (snake, camel) => {
      const v = t[snake] ?? t[camel];
      if (v != null && String(v).trim() !== "") return String(v).trim();
      return null;
    };
    const col = (snake, camel) => {
      const v = c[snake] ?? c[camel];
      if (v != null && String(v).trim() !== "") return String(v).trim();
      return null;
    };

    const energy = root("energy_color", "energyColor") || col("ui_color_1", "uiColor1") || "#e3e32f";
    const timeC = root("time_color", "timeColor") || col("option_color", "optionColor") || "#6fa7e3";
    const heart = root("heart_color", "heartColor") || col("ui_color_2", "uiColor2") || "#d8381f";
    const heading = root("heading_text_color", "headingTextColor") || col("ui_color_1", "uiColor1") || "#4fd1c0";
    const question = root("question_color", "questionColor") || col("text_color", "textColor") || "#b2a7a7";
    const btnText = root("button_text_color", "buttonTextColor") || col("option_text_color", "optionTextColor") || "#cdcd9f";
    const optionBg =
      root("option_bg_color", "optionBgColor") || col("option_bg_color", "optionBgColor") || "rgba(0, 0, 0, 0.1)";
    const choiceMainBox =
      root("main_color_box", "mainColorBox") || col("main_color_box", "mainColorBox") || "rgba(0, 0, 0, 0.06)";
    const dykBg = root("do_u_know_bg_color", "doUKnowBgColor") || col("option_color", "optionColor") || "#308f63";
    const dykText =
      root("do_u_know_bg_text_color", "doUKnowBgTextColor") || col("text_color", "textColor") || "#000000";
    const surface = col("ui_color_2", "uiColor2") || "#0d2b35";
    const bodyText = col("text_color", "textColor") || question;
    const tagline = root("energy_color", "energyColor") || col("ui_color_1", "uiColor1") || "#f5c842";
    const primaryBtnBg = root("button_color", "buttonColor") || col("ui_color_1", "uiColor1") || "#000000";
    const primaryBtnText =
      root("button_text_color", "buttonTextColor") || col("option_text_color", "optionTextColor") || "#ffffff";
    const onAccent = surface;
    const boxBg = root("box_bg_color", "boxBgColor") || primaryBtnBg;
    const boxText = root("box_text_color", "boxTextColor") || primaryBtnText;

    return {
      "--hs-energy-color": energy,
      "--hs-time-color": timeC,
      "--hs-heart-color": heart,
      "--hs-heading-text": heading,
      "--hs-question-text": question,
      "--hs-button-text": btnText,
      "--hs-option-answer-text": btnText,
      "--hs-choice-main-bg": choiceMainBox,
      "--hs-choice-option-bg": optionBg,
      "--hs-dyk-bg": dykBg,
      "--hs-dyk-text": dykText,
      "--hs-accent": heading,
      "--hs-surface-bg": surface,
      "--hs-body-text": bodyText,
      "--hs-muted-text": question,
      "--hs-tagline-text": tagline,
      "--hs-on-accent": onAccent,
      "--hs-primary-btn-bg": primaryBtnBg,
      "--hs-primary-btn-text": primaryBtnText,
      "--hs-badge-bg": energy,
      "--hs-badge-text": onAccent,
      "--hs-box-bg": boxBg,
      "--hs-box-text": boxText,
    };
  }, [themeData]);
}

export default function HealthSprint() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const sessionIdFromStore = useSelector(selectSessionId);
  const organizationIdFromStore = useSelector(selectOrganizationId);
  const { status, user } = useSelector((state) => state.auth);
  const themeData = useSelector((s) => s.theme?.data);
  const themeStatus = useSelector((s) => s.theme?.status);
  
  // Detect if running from mobile app wrapper
  const fromMobileAppFlag = useMemo(
    () => isFromMobileApp(location.search, user),
    [location.search, user]
  );

  const iconUrls = useThemeIconUrls(themeData);
  const themeVars = useHealthSprintThemeVars(themeData);

  const [screen, setScreen] = useState("game"); // game | dyk | recovery
  const screenRef = useRef(screen);
  /** False until `fetchStageProgress` + resume `setScreen` finish — avoids HUD tour opening then closing when ORG lands on DYK/recovery. */
  const [stageHydrated, setStageHydrated] = useState(false);
  /** null until stage row loaded; true = show HUD intro (DB points/total_score 0 & no saved progress). */
  const [hudTourOfferFromDb, setHudTourOfferFromDb] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const gameBootstrappedRef = useRef(false);
  const insufficientQuestionsRef = useRef(false);
  const gameRootRef = useRef(null);
  const wasGameLoadingRef = useRef(true);
  const activeDayDotRef = useRef(null);
  const choiceCommitTimerRef = useRef(null);
  const choiceCommittingRef = useRef(false);
  const impactBeforeDykTimerRef = useRef(null);
  const tourMeterIconsGroupRef = useRef(null);
  const tourMeterBarsGroupRef = useRef(null);
  const tourEnergyBarBtnRef = useRef(null);
  const tourEnergyDrinkRef = useRef(null);
  const tourSoundRef = useRef(null);
  const tourDayCounterRef = useRef(null);
  /** Set true only after a successful auto-open on `screen === "game"` (not when timeout is cancelled or resume moves to DYK first). */
  const hudTourAutoOpenConsumedRef = useRef(false);
  /** If intro not done, we stay on `game` for the HUD tour first; then apply saved `dyk` / `recovery`. */
  const resumeAfterHudTourRef = useRef(null);

  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  const [days, setDays] = useState([]);
  const [previewIdx, setPreviewIdx] = useState(null);
  /** During pre-commit delay, index of the option showing commit styling. */
  const [committingChoiceIdx, setCommittingChoiceIdx] = useState(null);
  /** After scores apply: show impact flash on game screen before opening DYK. */
  const [impactBeforeDyk, setImpactBeforeDyk] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mqNarrow = window.matchMedia("(max-width: 820px)");
    const mqLandShort = window.matchMedia(
      "(orientation: landscape) and (max-height: 560px)"
    );
    const mqTouchUi = window.matchMedia("(pointer: coarse), (hover: none)");

    const syncMobile = () => {
      const landPhone =
        mqLandShort.matches &&
        (mqTouchUi.matches || "ontouchstart" in window);
      setIsMobile(mqNarrow.matches || landPhone);
    };

    syncMobile();
    mqNarrow.addEventListener("change", syncMobile);
    mqLandShort.addEventListener("change", syncMobile);
    mqTouchUi.addEventListener("change", syncMobile);
    window.addEventListener("resize", syncMobile);
    window.addEventListener("orientationchange", syncMobile);
    return () => {
      mqNarrow.removeEventListener("change", syncMobile);
      mqLandShort.removeEventListener("change", syncMobile);
      mqTouchUi.removeEventListener("change", syncMobile);
      window.removeEventListener("resize", syncMobile);
      window.removeEventListener("orientationchange", syncMobile);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (choiceCommitTimerRef.current) {
        clearTimeout(choiceCommitTimerRef.current);
        choiceCommitTimerRef.current = null;
      }
      if (impactBeforeDykTimerRef.current) {
        clearTimeout(impactBeforeDykTimerRef.current);
        impactBeforeDykTimerRef.current = null;
      }
    };
  }, []);

  const [day, setDay] = useState(0);
  const [energy, setEnergy] = useState(INITIAL_METERS);
  const [time, setTime] = useState(INITIAL_METERS);
  const [health, setHealth] = useState(INITIAL_METERS);
  const [powerups, setPowerups] = useState({ bar: 2, drink: 2 });
  const [wellnessUsed, setWellnessUsed] = useState(false);
  const [pendingDyk, setPendingDyk] = useState([]);
  const [stageId, setStageId] = useState(null);
  /** 1-based option index per question answered, e.g. [1, 3, 2] */
  const [answersSelected, setAnswersSelected] = useState([]);

  useEffect(() => {
    choiceCommittingRef.current = false;
    if (choiceCommitTimerRef.current) {
      clearTimeout(choiceCommitTimerRef.current);
      choiceCommitTimerRef.current = null;
    }
    if (impactBeforeDykTimerRef.current) {
      clearTimeout(impactBeforeDykTimerRef.current);
      impactBeforeDykTimerRef.current = null;
    }
    setCommittingChoiceIdx(null);
    setImpactBeforeDyk(false);
  }, [day]);

  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.5);

  const [hudTourOpen, setHudTourOpen] = useState(false);
  const [hudTourStep, setHudTourStep] = useState(0);
  const [hudTourHole, setHudTourHole] = useState(null);
  /** Tooltip below target when true, above when false. */
  const [hudTourCardBelow, setHudTourCardBelow] = useState(true);

  const { flash, trigger } = useFlashMeter();

  /** Latest report id for PUT updates (synced from API POST response; avoids duplicate POST before Redux updates). */
  const reportIdRef = useRef(user?.reportId ?? null);
  useEffect(() => {
    reportIdRef.current = user?.reportId ?? null;
  }, [user?.reportId]);
  /** Serialize sendReport calls: several `persistStageToServer` calls can fire close together
   *  (fast day answers, meter + progress updates) — without this, two calls can both see no
   *  reportId yet and both POST, creating two report rows for the same user. */
  const reportSyncPipelineRef = useRef(Promise.resolve());

 

  const questionLoopRef = useRef(null);
  /** Resume question-screen loop after power-up SFX (reads latest mute/URL from `audioUiRefs`). */
  const resumeQuestionBackgroundRef = useRef(() => {});
  const audioUiRefs = useRef({
    muted: false,
    screen: "game",
    hasTheme: false,
    loopUrl: "",
    volume: 0.5,
  });
  const demoSubscribeShownRef = useRef(false);
  const powerupClickLockRef = useRef(false);

  /**
   * Admin Rules → Sound (`sound_toogle` in themesdata). Only when true: show speaker + volume and play question music/SFX.
   * Explicit false → hidden + silent. Missing key → on (older themes without the field).
   */
  const questionSoundEnabled = useMemo(() => {
    const v = themeData?.sound_toogle ?? themeData?.sound_toggle;
    if (v === true) return true;
    if (v === false) return false;
    if (v === undefined || v === null) return true;
    const s = String(v).trim().toLowerCase();
    if (s === "") return true;
    return s === "true" || s === "1";
  }, [themeData?.sound_toogle, themeData?.sound_toggle]);

  const hudTourSteps = useMemo(() => {
    const row = (id, ref, copy, pad = HUD_TOUR_PAD) => ({ id, ref, copy, pad });
    const out = [
      row("meterIconsGroup", tourMeterIconsGroupRef, HUD_TOUR_STEP_COPY.meterIconsGroup, HUD_TOUR_PAD_METER_ICONS),
      row("meterBarsGroup", tourMeterBarsGroupRef, HUD_TOUR_STEP_COPY.meterBarsGroup, HUD_TOUR_PAD_METER_BARS),
      row("energyBarPowerup", tourEnergyBarBtnRef, HUD_TOUR_STEP_COPY.energyBarPowerup, HUD_TOUR_PAD_CIRCLE),
      row("energyDrink", tourEnergyDrinkRef, HUD_TOUR_STEP_COPY.energyDrink, HUD_TOUR_PAD_CIRCLE),
    ];
    if (questionSoundEnabled) out.push(row("sound", tourSoundRef, HUD_TOUR_STEP_COPY.sound, HUD_TOUR_PAD_CIRCLE));
    out.push(row("dayCounter", tourDayCounterRef, HUD_TOUR_STEP_COPY.dayCounter));
    return out;
  }, [questionSoundEnabled]);

  const defaultQuestionSoundUrl = useMemo(
    () => resolveThemeDefaultQuestionSoundUrl(themeData),
    [
      themeData?.themename,
      themeData?.default_question_sound,
      themeData?.defaultQuestionSound,
      themeData?.default_music,
      themeData?.defaultMusic,
    ]
  );

  /** Theme media from admin Add Content (S3 filenames in themesdata). */
  const clickAnswerSoundUrl = useMemo(
    () => resolveThemeSoundUrl(themeData, "click_answer_sound", "clickAnswerSound"),
    [themeData?.click_answer_sound, themeData?.clickAnswerSound, themeData?.themename]
  );

  /** Short UI click from theme `click_answer_sound` (S3 URL). No URL → no sound. */
  const playOneShot = useCallback(
    (src) => {
      if (!questionSoundEnabled) return;
      const vol = Math.max(0, Math.min(1, volume));
      if (vol <= 0 || !hasAudioUrl(src)) return;
      void resumeAudioContext();
      const url = String(src).trim();
      try {
        const a = new Audio(url);
        a.volume = vol;
        void a.play().catch(() => {});
      } catch {
        /* ignore */
      }
    },
    [volume, questionSoundEnabled]
  );

  const questionLimit = useMemo(() => {
    const n = parseInt(String(themeData?.no_of_questions ?? themeData?.noOfQuestion ?? "10"), 10);
    return Number.isFinite(n) && n > 0 ? Math.min(n, 999) : 10;
  }, [themeData?.no_of_questions, themeData?.noOfQuestion]);

  const gameDays = useMemo(() => {
    const lim = Math.min(days.length, questionLimit);
    return days.slice(0, lim);
  }, [days, questionLimit]);

  const totalDays = gameDays.length;

  const syncReportAfterPersist = useCallback(
    (updateRes, userData, metersFallback) => {
      const run = async () => {
      const role = userData?.role;
      if (!userData?.userId || role === "demobypass") return;

      const sid = sessionIdFromStore ?? userData?.sessionId;
      const oid = organizationIdFromStore ?? userData?.organizationId;
      if (!sid || !oid) return;

      const ridFromUpdate =
        updateRes?.stage?.reportId ?? updateRes?.reportId ?? updateRes?.reportid;
      if (ridFromUpdate != null && String(ridFromUpdate).trim() !== "") {
        const r = String(ridFromUpdate).trim();
        reportIdRef.current = r;
        dispatch(mergeReportId(r));
      }

      const st = updateRes?.stage ?? updateRes ?? {};
      let points =
        st.points != null && !Number.isNaN(Number(st.points))
          ? Number(st.points)
          : metersFallback != null
            ? Math.round(
                Number(metersFallback.energy) +
                  Number(metersFallback.time) +
                  Number(metersFallback.health)
              )
            : 0;

      const timeRaw =
        st.time != null && String(st.time).trim() !== ""
          ? String(st.time).trim()
          : "00:00:00";
      const timeSec = normalizeReportTimeValue(timeRaw);

      const userType =
        st.user_type != null && String(st.user_type).trim() !== ""
          ? String(st.user_type).trim()
          : String(userData.role || "");

      let effectiveReportId = reportIdRef.current || userData?.reportId;
      if (
        effectiveReportId != null &&
        userData?.userId != null &&
        String(effectiveReportId).trim() === String(userData.userId).trim()
      ) {
        effectiveReportId = null;
      }
      const ridStr = resolveReportIdForSendReport({
        reportId: effectiveReportId,
      });

      const reportPayload = {
        sessionId: sid,
        role: userData.role,
        token: resolveReportsAuthToken(userData.token, adminToken),
        name: userData.name,
        email: userData.email || null,
        userId: userData.userId,
        gameId: userData.gameId || "",
        organizationId: oid,
        points,
        time: timeSec,
        reportId: ridStr || null,
        ans: "",
        user_type: userType,
        owner: userType,
      };

      console.log("[HealthSprint] sendReport (after updateStage)", {
        userId: userData.userId,
        role: userData.role,
        reportId: reportPayload.reportId,
        points: reportPayload.points,
        timeSecondsForClient: timeSec,
        user_type: userType,
      });

      try {
        const sres = await sendReport(reportPayload);
        const rid = extractReportIdFromResponse(sres, userData?.userId);
        if (rid != null && rid !== "") {
          reportIdRef.current = rid;
          dispatch(mergeReportId(rid));
        }
      } catch (e) {
        console.warn("[HealthSprint] report sync failed:", e?.message || e);
      }
      };
      const next = reportSyncPipelineRef.current.then(run, run);
      reportSyncPipelineRef.current = next.catch(() => {});
      return next;
    },
    [sessionIdFromStore, organizationIdFromStore, dispatch, adminToken]
  );

  const persistStageToServer = useCallback(
    async (body) => {
      if (!user?.userId || !adminToken || stageId == null) return null;
      const res = await updateStage({
        userId: user.userId,
        token: adminToken,
        stageId,
        ...body,
      });
      const meters =
        body.energy != null && body.time != null && body.health != null
          ? { energy: body.energy, time: body.time, health: body.health }
          : null;
      await syncReportAfterPersist(res, user, meters);
      return res;
    },
    [user, adminToken, stageId, syncReportAfterPersist]
  );

  const finishHudTour = useCallback(() => {
    setHudTourOpen(false);
    setHudTourStep(0);
    setHudTourHole(null);

    const pending = resumeAfterHudTourRef.current;
    resumeAfterHudTourRef.current = null;
    if (pending?.screen === "dyk") {
      if (Array.isArray(pending.pendingDyk) && pending.pendingDyk.length > 0) {
        setPendingDyk(pending.pendingDyk);
      }
      setScreen("dyk");
    } else if (pending?.screen === "recovery") {
      setPendingDyk([]);
      setScreen("recovery");
    }
  }, [user?.userId]);

  const measureHudTourStep = useCallback(() => {
    if (!hudTourOpen) {
      setHudTourHole(null);
      return;
    }
    const step = hudTourSteps[hudTourStep];
    const el = step?.ref?.current;
    if (!el || !(el instanceof Element)) {
      setHudTourHole(null);
      return;
    }
    void el.offsetWidth;
    const r = el.getBoundingClientRect();
    let padSpec = step.pad;
    /* Mobile: rectangular spotlight for power-ups + sound (not tight circle pad). */
    if (isMobile && isHudTourBottomControlStep(step.id)) {
      padSpec = HUD_TOUR_PAD;
    }
    let { top: pt, right: pr, bottom: pb, left: pl } = resolveHudTourPad(padSpec);
    /* Scaled HUD + narrow columns: tighter horizontal pad so spotlight does not cover the next column. */
    if (isMobile && (step.id === "meterIconsGroup" || step.id === "meterBarsGroup")) {
      pt = Math.min(pt, 8);
      pb = Math.min(pb, 8);
      if (step.id === "meterIconsGroup") {
        pr = Math.min(pr, 2);
        pl = Math.min(pl, 6);
      } else {
        pl = Math.min(pl, 2);
        pr = Math.min(pr, 6);
      }
    }
    const rawTop = r.top - pt;
    const rawLeft = r.left - pl;
    const rawWidth = r.width + pl + pr;
    const rawHeight = r.height + pt + pb;
    const holeInset = isMobile ? Math.max(HUD_TOUR_VIEWPORT_INSET, 12) : HUD_TOUR_VIEWPORT_INSET;
    const { top, left, width, height } = clampHudTourHoleToViewport(
      rawLeft,
      rawTop,
      rawWidth,
      rawHeight,
      holeInset
    );
    /* True center of the target element (caret + card should align to this; clamped hole rect can shift). */
    const targetCenterX = r.left + r.width / 2;
    const targetCenterY = r.top + r.height / 2;
    setHudTourHole({ top, left, width, height, targetCenterX, targetCenterY });
    const approxCard = isMobile ? 190 : 210;
    const spaceBelow = window.innerHeight - (top + height);
    const vwM = typeof window !== "undefined" ? window.innerWidth : 0;
    const vhM = typeof window !== "undefined" ? window.innerHeight : 0;
    const landPhone = isMobile && vwM > vhM;
    /* Landscape + bottom controls: open popover above (or side in layout useMemo), not below the dock. */
    if (landPhone && isHudTourBottomControlStep(step.id)) {
      setHudTourCardBelow(false);
    } else {
      setHudTourCardBelow(spaceBelow >= approxCard + 20 || top < approxCard);
    }
  }, [hudTourOpen, hudTourStep, hudTourSteps, isMobile]);

  useLayoutEffect(() => {
    if (!hudTourOpen) return;
    const step = hudTourSteps[hudTourStep];
    const el = step?.ref?.current;
    if (step?.id && el instanceof Element) {
      if (isMobile && HUD_TOUR_MOBILE_SCROLL_STEP_IDS.has(step.id)) {
        /* `auto` so layout updates before measure in this same frame. */
        el.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" });
      } else if (isMobile && step.id === "dayCounter") {
        /* Last step: bring HUD day into view near the top with the Start card. */
        el.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
      } else if (step.id === "meterIconsGroup" || step.id === "meterBarsGroup") {
        /* Top HUD: scroll into view on all breakpoints so the ring is not flush against viewport corners. */
        el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
      }
    }
    measureHudTourStep();
  }, [hudTourOpen, hudTourStep, measureHudTourStep, isMobile, hudTourSteps]);

  useEffect(() => {
    if (!hudTourOpen) return;
    const onResize = () => measureHudTourStep();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [hudTourOpen, measureHudTourStep]);

  useEffect(() => {
    if (hudTourAutoOpenConsumedRef.current) return;
    if (hudTourOfferFromDb !== true) {
      if (hudTourOfferFromDb === false) hudTourAutoOpenConsumedRef.current = true;
      return;
    }
    if (!stageHydrated || isLoading || gameDays.length === 0 || screen !== "game") return;
    const uid = user?.userId;
    if (uid == null || String(uid).trim() === "") return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      if (screenRef.current !== "game") return;
      hudTourAutoOpenConsumedRef.current = true;
      setHudTourOpen(true);
      setHudTourStep(0);
    }, 100);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [stageHydrated, hudTourOfferFromDb, isLoading, gameDays.length, screen, user?.userId]);

  useEffect(() => {
    if (hudTourOpen && screen !== "game") {
      setHudTourOpen(false);
      setHudTourStep(0);
      setHudTourHole(null);
    }
  }, [hudTourOpen, screen]);

  /** Per-question `music` from DB (S3) when set; otherwise theme `default_question_sound`. Empty when admin Sound is off. */
  const questionLoopMusicUrl = useMemo(() => {
    if (!questionSoundEnabled) return "";
    const row = gameDays[day];
    const per = resolveQuestionMusicLoopUrl(row);
    if (per && String(per).trim() !== "") return per;
    return defaultQuestionSoundUrl;
  }, [gameDays, day, defaultQuestionSoundUrl, questionSoundEnabled]);

  useEffect(() => {
    audioUiRefs.current = {
      muted,
      screen,
      hasTheme: !!themeData,
      loopUrl: questionLoopMusicUrl,
      volume,
    };
  }, [muted, screen, themeData, questionLoopMusicUrl, volume, questionSoundEnabled]);

  const pauseQuestionBackgroundMusic = useCallback(() => {
    try {
      questionLoopRef.current?.pause();
    } catch {
      /* ignore */
    }
  }, []);

  /** Energy bar / drink: duck background loop, play theme S3 MP3, then resume loop. */
  const playPowerupSound = useCallback(
    (kind) => {
      if (!questionSoundEnabled) return;
      void resumeAudioContext();
      pauseQuestionBackgroundMusic();
      const resume = () => {
        resumeQuestionBackgroundRef.current();
      };
      const vol = Math.max(0, Math.min(1, audioUiRefs.current.volume));
      if (vol <= 0) {
        resume();
        return;
      }
      const isDrink = kind === "drink";
      const src = isDrink
        ? resolveThemeSoundUrl(themeData, "energy_drink_sound", "energyDrinkSound")
        : resolveThemeSoundUrl(themeData, "energy_bar_sound", "energyBarSound");
      if (!hasAudioUrl(src)) {
        resume();
        return;
      }
      try {
        const sfx = new Audio(String(src).trim());
        sfx.volume = vol;
        sfx.preload = "auto";
        sfx.onended = resume;
        sfx.onerror = resume;
        void sfx.play().catch(() => resume());
      } catch {
        resume();
      }
    },
    [pauseQuestionBackgroundMusic, themeData, questionSoundEnabled]
  );

  /** Looping background music on the main choice screen (per-question or theme default from S3). */
  useEffect(() => {
    const stopHtml = () => {
      if (questionLoopRef.current) {
        try {
          questionLoopRef.current.pause();
          questionLoopRef.current.src = "";
        } catch {
          /* ignore */
        }
        questionLoopRef.current = null;
      }
    };

    resumeQuestionBackgroundRef.current = () => {};

    if (!questionSoundEnabled || muted || screen !== "game" || !themeData) {
      stopHtml();
      return undefined;
    }

    const url = (questionLoopMusicUrl || "").trim();
    if (!hasAudioUrl(url)) {
      stopHtml();
      return () => {
        stopHtml();
        resumeQuestionBackgroundRef.current = () => {};
      };
    }

    void resumeAudioContext();

    const resumeBg = () => {
      const r = audioUiRefs.current;
      if (!questionSoundEnabled || r.muted || r.screen !== "game" || !r.hasTheme) return;
      void resumeAudioContext();
      const a = questionLoopRef.current;
      if (a) {
        a.volume = r.volume;
        void a.play().catch(() => {});
      }
    };
    resumeQuestionBackgroundRef.current = resumeBg;

    const audio = new Audio(url);
    audio.loop = true;
    audio.volume = volume;
    questionLoopRef.current = audio;
    void audio.play().catch(() => {
      stopHtml();
    });

    return () => {
      stopHtml();
      resumeQuestionBackgroundRef.current = () => {};
    };
  }, [screen, muted, questionLoopMusicUrl, themeData, volume, questionSoundEnabled]);

  useEffect(() => {
    const a = questionLoopRef.current;
    if (a) a.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (screen !== "game") {
      wasGameLoadingRef.current = isLoading;
      return;
    }
    if (!isLoading && wasGameLoadingRef.current) {
      const root = gameRootRef.current;
      if (root) root.scrollTop = 0;
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    wasGameLoadingRef.current = isLoading;
  }, [isLoading, screen]);

  useEffect(() => {
    insufficientQuestionsRef.current = false;
  }, [themeData?.themename]);

  useEffect(() => {
    if (isLoading) return;
    if (!user?.userId || !themeData) return;
    const limit = parseInt(String(themeData?.no_of_questions ?? "10"), 10);
    const lim = Number.isFinite(limit) && limit > 0 ? Math.min(limit, 999) : 10;
    if (days.length >= lim) return;
    if (insufficientQuestionsRef.current) return;
    insufficientQuestionsRef.current = true;
    const bg = themeVars["--hs-dyk-bg"];
    const fg = themeVars["--hs-dyk-text"];
    void Swal.fire({
      icon: "warning",
      title: "Not enough questions",
      text: "Please add questions from admin.",
      background: bg,
      color: fg,
      confirmButtonText: "OK",
      customClass: {
        popup: "swal2-popup-powerup-dyk",
        title: "swal2-powerup-title-dyk",
        htmlContainer: "swal2-powerup-body-dyk",
      },
      didOpen: (popup) => {
        Object.entries(themeVars).forEach(([key, val]) => {
          popup.style.setProperty(key, String(val));
        });
        const btn = popup.querySelector(".swal2-confirm");
        if (btn) {
          btn.style.backgroundColor = "rgba(255, 255, 255, 0.22)";
          btn.style.color = String(fg);
          btn.style.border = "1px solid rgba(255, 255, 255, 0.45)";
        }
        const iconEl = popup.querySelector(".swal2-icon");
        if (iconEl) {
          iconEl.style.borderColor = String(fg);
          iconEl.style.color = String(fg);
        }
      },
    }).then(() => navigate("/rules", { replace: true }));
  }, [isLoading, days.length, themeData, user?.userId, navigate, themeVars]);

  useEffect(() => {
    attachAudioGestureOnce();
  }, []);

  // Load theme media (S3 icon paths) for this org/session — same source as admin Add Content.
  useEffect(() => {
    if (!adminToken) return;
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch, adminToken]);

  /** Header Back → Rules (progress stays in DB + session; re-entering the game resumes via fetchStageProgress). */
  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [dispatch, status, user]);

  // Stage row is created on the Rules page (`initQuestions` + question ids in session). Here we only load questions by id and update progress — no INSERT.
  useEffect(() => {
    if (!user?.userId || !adminToken) {
      setIsLoading(false);
      return;
    }
    if (themeStatus === "failed") {
      setIsLoading(false);
      setFetchError((prev) => prev || "Failed to load theme");
      return;
    }
    if (themeStatus !== "succeeded") return;

    const themeName =
      themeData?.themename != null && String(themeData.themename).trim() !== ""
        ? String(themeData.themename).trim()
        : null;
    if (!themeName) return;

    const ctx = getHealthSprintStageContext();
    if (!ctx || !Number.isFinite(ctx.stageId) || !themeNamesMatch(ctx.themeName, themeName)) {
      setIsLoading(false);
      navigate("/rules", { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setFetchError(null);
        setIsLoading(true);
        setStageHydrated(false);
        setStageId(ctx.stageId);
        setDays([]);

        const qids = ctx.questionIds || [];
        const data = await fetchUserQuestions({
          userId: user.userId,
          token: adminToken,
          themeName,
          questionIds: qids,
        });
        if (cancelled) return;
        setDays(parseDaysFromQuestions(data));
      } catch (err) {
        if (!cancelled) {
          setFetchError(err?.message || "Failed to load questions");
          setDays([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.userId, adminToken, themeStatus, themeData?.themename, navigate]);

  useEffect(() => {
    if (days.length === 0) {
      gameBootstrappedRef.current = false;
      setStageHydrated(false);
      setHudTourOfferFromDb(null);
      resumeAfterHudTourRef.current = null;
    }
  }, [days.length]);

  useEffect(() => {
    gameBootstrappedRef.current = false;
    setStageHydrated(false);
    setHudTourOfferFromDb(null);
    resumeAfterHudTourRef.current = null;
  }, [themeData?.themename, user?.userId]);

  // After questions + stage row exist: restore from DB or start fresh (resume after refresh).
  useEffect(() => {
    if (isLoading || gameDays.length === 0 || stageId == null || !user?.userId || !adminToken) return;
    if (gameBootstrappedRef.current) return;

    let cancelled = false;
    let stageSnapshotForTour = null;
    (async () => {
      try {
        const data = await fetchStageProgress({
          userId: user.userId,
          token: adminToken,
          stageId,
        });
        if (cancelled) return;
        const st = data?.stage;
        stageSnapshotForTour = st ?? null;
        const ridFromStage = st?.reportId ?? st?.report_id ?? st?.reportid;
        if (ridFromStage != null && String(ridFromStage).trim() !== "") {
          const rid = String(ridFromStage).trim();
          reportIdRef.current = rid;
          dispatch(mergeReportId(rid));
        }

        console.log("[HealthSprint] getStageProgress", {
          userId: user.userId,
          stageId,
          sessionId: sessionIdFromStore ?? user.sessionId,
          organizationId: organizationIdFromStore ?? user.organizationId,
          reportIdFromStage: ridFromStage ?? null,
          reportIdRefAfterHydrate: reportIdRef.current,
          userReportIdFromLogin: user?.reportId ?? null,
          stage: st && {
            id: st.id,
            points: st.points,
            total_score: st.total_score,
            time: st.time,
            user_type: st.user_type,
            current: st.current,
            energy_score: st.energy_score,
            time_score: st.time_score,
            health_score: st.health_score,
          },
        });

        if (st?.questionsParsed?.resume?.completed === true) {
          navigate("/thankyou", {
            replace: true,
            state: {
              energy: clamp(st.energy_score ?? INITIAL_METERS),
              time: clamp(st.time_score ?? INITIAL_METERS),
              health: clamp(st.health_score ?? INITIAL_METERS),
              stageId,
            },
          });
          return;
        }
        if (st && hasSavedProgress(st)) {
          let answers = [];
          try {
            answers = JSON.parse(st.answers || "[]");
          } catch {
            answers = [];
          }
          if (Array.isArray(answers) && answers.length > gameDays.length) {
            answers = answers.slice(0, gameDays.length);
          }
          const curRaw = parseInt(String(st.current ?? ""), 10);
          let safeDay = Number.isFinite(curRaw) ? curRaw : 0;
          const maxDay = Math.max(0, gameDays.length - 1);
          safeDay = Math.min(Math.max(0, safeDay), maxDay);

          setAnswersSelected(Array.isArray(answers) ? answers : []);
          setEnergy(clamp(st.energy_score ?? INITIAL_METERS));
          setTime(clamp(st.time_score ?? INITIAL_METERS));
          setHealth(clamp(st.health_score ?? INITIAL_METERS));
          setDay(safeDay);

          const r = st.questionsParsed?.resume || {};
          if (r.powerups && typeof r.powerups.bar === "number" && typeof r.powerups.drink === "number") {
            setPowerups({ bar: r.powerups.bar, drink: r.powerups.drink });
          } else {
            setPowerups({ bar: 2, drink: 2 });
          }
          if (typeof r.wellnessUsed === "boolean") setWellnessUsed(r.wellnessUsed);
          else setWellnessUsed(false);

          const scr = r.screen || "game";
          const dykList =
            scr === "dyk"
              ? Array.isArray(r.pendingDyk) && r.pendingDyk.length > 0
                ? r.pendingDyk
                : gameDays[safeDay]?.dyk || []
              : [];

          if (shouldOfferHudIntroTourFromStage(st) && (scr === "dyk" || scr === "recovery")) {
            resumeAfterHudTourRef.current =
              scr === "dyk"
                ? { screen: "dyk", pendingDyk: dykList }
                : { screen: "recovery", pendingDyk: [] };
            setPendingDyk([]);
            setScreen("game");
          } else if (scr === "dyk") {
            setPendingDyk(dykList);
            setScreen("dyk");
          } else if (scr === "recovery") {
            setScreen("recovery");
          } else {
            setPendingDyk([]);
            setScreen("game");
          }
        } else {
          setScreen("game");
          setDay(0);
          setEnergy(INITIAL_METERS);
          setTime(INITIAL_METERS);
          setHealth(INITIAL_METERS);
          setPowerups({ bar: 2, drink: 2 });
          setWellnessUsed(false);
          setPendingDyk([]);
          setAnswersSelected([]);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("Could not restore stage:", err.message);
          setScreen("game");
          setDay(0);
          setEnergy(INITIAL_METERS);
          setTime(INITIAL_METERS);
          setHealth(INITIAL_METERS);
          setPowerups({ bar: 2, drink: 2 });
          setWellnessUsed(false);
          setPendingDyk([]);
          setAnswersSelected([]);
        }
      } finally {
        if (!cancelled) {
          gameBootstrappedRef.current = true;
          setHudTourOfferFromDb(shouldOfferHudIntroTourFromStage(stageSnapshotForTour));
          setStageHydrated(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isLoading,
    gameDays,
    stageId,
    user,
    adminToken,
    themeData?.themename,
    navigate,
    dispatch,
  ]);

  const dayData = gameDays[day] || null;

  const meterPreviewChoiceIdx =
    committingChoiceIdx !== null ? committingChoiceIdx : previewIdx;
  const displayEnergy =
    meterPreviewChoiceIdx !== null && dayData?.choices[meterPreviewChoiceIdx]
      ? clamp(energy + dayData.choices[meterPreviewChoiceIdx].energy)
      : energy;
  const displayTime =
    meterPreviewChoiceIdx !== null && dayData?.choices[meterPreviewChoiceIdx]
      ? clamp(time + dayData.choices[meterPreviewChoiceIdx].time)
      : time;
  const displayHealth =
    meterPreviewChoiceIdx !== null && dayData?.choices[meterPreviewChoiceIdx]
      ? clamp(health + dayData.choices[meterPreviewChoiceIdx].health)
      : health;

  const dykPopupText = useMemo(() => {
    const fromChoice = getDykTextForSelectedChoice(dayData, answersSelected, day);
    if (fromChoice) return fromChoice;
    if (Array.isArray(pendingDyk) && pendingDyk.length === 1) {
      const t = String(pendingDyk[0]).trim();
      if (t) return t;
    }
    return "";
  }, [dayData, answersSelected, day, pendingDyk]);

  const lastChosenIdx0 =
    answersSelected.length > 0 ? answersSelected[answersSelected.length - 1] - 1 : null;

  const handleChoice = useCallback(
    (idx) => {
      if (!dayData) return;
      const choice = dayData.choices[idx];
      if (!choice) return;

      const nextEnergy = clamp(energy + choice.energy);
      const nextTime = clamp(time + choice.time);
      const nextHealth = clamp(health + choice.health);
      const newAnswers = [...answersSelected, idx + 1];

      const sid = sessionIdFromStore ?? user?.sessionId;
      if (
        newAnswers.length === 1 &&
        sid != null &&
        String(sid).toLowerCase().startsWith("demo") &&
        !demoSubscribeShownRef.current
      ) {
        demoSubscribeShownRef.current = true;
        void Swal.fire("Subscribe to any PLAN to play with your peers.", "", "success").then(() => {
          navigateExternalUrl("https://extramileplay.com/plans", fromMobileAppFlag, "assign");
        });
      }

      /*
       * Meters already store committed values below; clear preview so displayEnergy/Time/Health
       * does not add the choice delta again (would spike during impact then revert when preview clears).
       */
      setPreviewIdx(null);
      setAnswersSelected(newAnswers);
      setEnergy(nextEnergy);
      setTime(nextTime);
      setHealth(nextHealth);

      const nextScreen =
        !wellnessUsed && (nextEnergy < 25 || nextTime < 25 || nextHealth < 25) ? "recovery" : "dyk";
      const chosenPopup = String(choice.popup || "").trim();
      const pendingDykForResume = chosenPopup ? [chosenPopup] : [];

      playOneShot(clickAnswerSoundUrl);

      // Flash meters (delta) like original.
      trigger({ energy: choice.energy, time: choice.time, health: choice.health });

      setPendingDyk(pendingDykForResume);

      if (nextScreen === "recovery") {
        if (impactBeforeDykTimerRef.current) {
          clearTimeout(impactBeforeDykTimerRef.current);
          impactBeforeDykTimerRef.current = null;
        }
        setImpactBeforeDyk(false);
        void persistStageToServer({
          answers: newAnswers,
          energy: nextEnergy,
          time: nextTime,
          health: nextHealth,
          currentDay: day,
          resume: {
            screen: nextScreen,
            wellnessUsed,
            powerups: { bar: powerups.bar, drink: powerups.drink },
            pendingDyk: pendingDykForResume,
          },
        });
        setScreen("recovery");
        setPreviewIdx(null);
        return;
      }

      void persistStageToServer({
        answers: newAnswers,
        energy: nextEnergy,
        time: nextTime,
        health: nextHealth,
        currentDay: day,
        resume: {
          screen: nextScreen,
          wellnessUsed,
          powerups: { bar: powerups.bar, drink: powerups.drink },
          pendingDyk: pendingDykForResume,
        },
      });

      setImpactBeforeDyk(true);
      if (impactBeforeDykTimerRef.current) {
        clearTimeout(impactBeforeDykTimerRef.current);
        impactBeforeDykTimerRef.current = null;
      }
      impactBeforeDykTimerRef.current = window.setTimeout(() => {
        impactBeforeDykTimerRef.current = null;
        setImpactBeforeDyk(false);
        setPreviewIdx(null);
        setScreen("dyk");
      }, METER_FLASH_DURATION_MS);
    },
    [
      dayData,
      energy,
      health,
      playOneShot,
      clickAnswerSoundUrl,
      time,
      trigger,
      wellnessUsed,
      answersSelected,
      day,
      persistStageToServer,
      powerups,
      sessionIdFromStore,
      user?.sessionId,
    ]
  );

  const scheduleChoiceCommit = useCallback(
    (idx) => {
      if (choiceCommittingRef.current) return;
      if (!dayData) return;
      const choice = dayData.choices[idx];
      if (!choice) return;
      choiceCommittingRef.current = true;
      setCommittingChoiceIdx(idx);
      setPreviewIdx(idx);
      if (choiceCommitTimerRef.current) clearTimeout(choiceCommitTimerRef.current);
      choiceCommitTimerRef.current = window.setTimeout(() => {
        choiceCommitTimerRef.current = null;
        choiceCommittingRef.current = false;
        setCommittingChoiceIdx(null);
        handleChoice(idx);
      }, CHOICE_COMMIT_DELAY_MS);
    },
    [dayData, handleChoice]
  );

  const handleContinue = useCallback(async () => {
    playOneShot(clickAnswerSoundUrl);
    const next = day + 1;
    if (totalDays <= 0 || next >= totalDays) {
      await persistStageToServer({
        answers: answersSelected,
        energy,
        time,
        health,
        currentDay: totalDays,
        resume: {
          screen: "game",
          wellnessUsed,
          powerups: { bar: powerups.bar, drink: powerups.drink },
          pendingDyk: [],
          completed: true,
        },
      });
      navigate("/thankyou", { replace: true, state: { energy, time, health, stageId } });
      return;
    }
    await persistStageToServer({
      answers: answersSelected,
      energy,
      time,
      health,
      currentDay: next,
      resume: {
        screen: "game",
        wellnessUsed,
        powerups: { bar: powerups.bar, drink: powerups.drink },
        pendingDyk: [],
      },
    });
    setDay(next);
    setScreen("game");
  }, [
    day,
    playOneShot,
    clickAnswerSoundUrl,
    totalDays,
    navigate,
    persistStageToServer,
    answersSelected,
    energy,
    time,
    health,
    wellnessUsed,
    powerups,
  ]);

  const takeWellness = useCallback(() => {
    playOneShot(clickAnswerSoundUrl);
    const nextE = clamp(energy + 10);
    const nextH = clamp(health + 10);
    const nextT = clamp(time - 10);
    setEnergy((v) => clamp(v + 10));
    setHealth((v) => clamp(v + 10));
    setTime((v) => clamp(v - 10));
    setWellnessUsed(true);
    setScreen("dyk");
    void persistStageToServer({
      answers: answersSelected,
      energy: nextE,
      time: nextT,
      health: nextH,
      currentDay: day,
      resume: {
        screen: "dyk",
        wellnessUsed: true,
        powerups: { bar: powerups.bar, drink: powerups.drink },
        pendingDyk: pendingDyk.length ? pendingDyk : dayData?.dyk || [],
      },
    });
  }, [
    playOneShot,
    clickAnswerSoundUrl,
    energy,
    health,
    time,
    answersSelected,
    day,
    persistStageToServer,
    powerups,
    pendingDyk,
    dayData?.dyk,
  ]);

  const skipWellness = useCallback(() => {
    playOneShot(clickAnswerSoundUrl);
    setWellnessUsed(true);
    setScreen("dyk");
    void persistStageToServer({
      answers: answersSelected,
      energy,
      time,
      health,
      currentDay: day,
      resume: {
        screen: "dyk",
        wellnessUsed: true,
        powerups: { bar: powerups.bar, drink: powerups.drink },
        pendingDyk: pendingDyk.length ? pendingDyk : dayData?.dyk || [],
      },
    });
  }, [
    playOneShot,
    clickAnswerSoundUrl,
    answersSelected,
    energy,
    time,
    health,
    day,
    persistStageToServer,
    powerups,
    pendingDyk,
    dayData?.dyk,
  ]);

  const showPowerupUsedPopup = useCallback(
    (kind) => {
      const bg = themeVars["--hs-dyk-bg"];
      const fg = themeVars["--hs-dyk-text"];
      const isBar = kind === "bar";
      /**
       * Defer open: opening Swal in the same event tick as the power-up click makes SweetAlert2
       * treat that click as “outside” the new modal → instant dismiss. Next task + small delay
       * also helps touch devices.
       */
      window.setTimeout(() => {
        void Swal.fire({
          title: isBar ? "Energy bar used" : "Energy drink used",
          html: isBar
            ? "<p>You used an <strong>energy bar</strong>.<br/>+10 Energy · +5 Health</p>"
            : "<p>You used an <strong>energy drink</strong>.<br/>+10 Energy · −5 Time Management</p>",
          background: bg,
          color: fg,
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: true,
          customClass: {
            popup: "swal2-popup-powerup-dyk",
            title: "swal2-powerup-title-dyk",
            htmlContainer: "swal2-powerup-body-dyk",
          },
          didOpen: (popup) => {
            Object.entries(themeVars).forEach(([key, val]) => {
              popup.style.setProperty(key, String(val));
            });
            popup.querySelectorAll("strong").forEach((el) => {
              el.style.color = fg;
            });
            const t = window.setTimeout(() => {
              Swal.close();
            }, 3000);
            popup.setAttribute("data-powerup-close-timer", String(t));
          },
          willClose: () => {
            const el = Swal.getPopup();
            const id = el?.getAttribute?.("data-powerup-close-timer");
            if (id) window.clearTimeout(Number(id));
          },
        });
      }, 50);
    },
    [themeVars]
  );

  const handlePowerup = useCallback(
    (type) => {
      if (powerupClickLockRef.current) return;
      if (powerups[type] <= 0) return;
      powerupClickLockRef.current = true;
      setTimeout(() => {
        powerupClickLockRef.current = false;
      }, 250);

      playPowerupSound(type === "bar" ? "bar" : "drink");
      setPowerups((p) => ({ ...p, [type]: p[type] - 1 }));
      showPowerupUsedPopup(type === "bar" ? "bar" : "drink");

      if (type === "bar") {
        const nextE = clamp(energy + 10);
        const nextH = clamp(health + 5);
        setEnergy((v) => clamp(v + 10));
        setHealth((v) => clamp(v + 5));
        trigger({ energy: +10, time: 0, health: +5 });
        void persistStageToServer({
          answers: answersSelected,
          energy: nextE,
          time,
          health: nextH,
          currentDay: day,
          powerup: { type: "energy_bar", day },
          resume: {
            screen: "game",
            wellnessUsed,
            powerups: { bar: powerups.bar - 1, drink: powerups.drink },
            pendingDyk: [],
          },
        });
      } else {
        const nextE = clamp(energy + 10);
        const nextT = clamp(time - 5);
        setEnergy((v) => clamp(v + 10));
        setTime((v) => clamp(v - 5));
        trigger({ energy: +10, time: -5, health: 0 });
        void persistStageToServer({
          answers: answersSelected,
          energy: nextE,
          time: nextT,
          health,
          currentDay: day,
          powerup: { type: "energy_drink", day },
          resume: {
            screen: "game",
            wellnessUsed,
            powerups: { bar: powerups.bar, drink: powerups.drink - 1 },
            pendingDyk: [],
          },
        });
      }
    },
    [
      playPowerupSound,
      showPowerupUsedPopup,
      powerups,
      trigger,
      energy,
      health,
      time,
      answersSelected,
      day,
      persistStageToServer,
      wellnessUsed,
    ]
  );

  const dayTrackerDots = useMemo(() => {
    return Array.from({ length: totalDays }, (_, i) => i + 1);
  }, [totalDays]);

  useEffect(() => {
    const el = activeDayDotRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [day, totalDays]);

  const recoveryLow = useMemo(() => {
    const low = [];
    if (energy < 25) low.push("Energy");
    if (time < 25) low.push("Time Management");
    if (health < 25) low.push("Health");
    return low;
  }, [energy, health, time]);

  const renderScenarioLines = (scenario) => {
    const lines = String(scenario || "")
      .split("\n")
      .map((l) => l.trimEnd())
      .filter((l) => l.trim().length > 0);

    return lines.map((l, i) => {
      const isLast = i === lines.length - 1;
      return (
        <div key={i} className={isLast ? "question" : undefined}>
          {l}
        </div>
      );
    });
  };

  const soundTitle = muted ? "Unmute background music" : "Mute background only — SFX still play";

  const hudTourStripRects = useMemo(() => {
    if (!hudTourHole) return null;
    const { top, left, width, height } = hudTourHole;
    const vw = typeof window !== "undefined" ? window.innerWidth : 0;
    const vh = typeof window !== "undefined" ? window.innerHeight : 0;
    const t = Math.max(0, top);
    const l = Math.max(0, left);
    const w = width;
    const h = height;
    return {
      top: { top: 0, left: 0, width: vw, height: t },
      left: { top: t, left: 0, width: l, height: h },
      right: { top: t, left: l + w, width: Math.max(0, vw - l - w), height: h },
      bottom: { top: t + h, left: 0, width: vw, height: Math.max(0, vh - t - h) },
    };
  }, [hudTourHole]);

  const onHudTourNext = useCallback(() => {
    if (hudTourStep >= hudTourSteps.length - 1) finishHudTour();
    else setHudTourStep((s) => s + 1);
  }, [finishHudTour, hudTourStep, hudTourSteps.length]);

  const onHudTourSkip = useCallback(() => finishHudTour(), [finishHudTour]);

  const hudTourCopy = hudTourSteps[hudTourStep]?.copy;
  const hudTourLast = hudTourStep >= hudTourSteps.length - 1;
  const hudTourStepId = hudTourSteps[hudTourStep]?.id;
  const hudTourGroupZoom =
    hudTourStepId === "meterIconsGroup" || hudTourStepId === "meterBarsGroup";

  /** Round focus ring on desktop only for sound / power-ups; mobile uses a rectangular spotlight. */
  const hudTourRoundRingDesktop =
    !isMobile &&
    (hudTourStepId === "sound" ||
      hudTourStepId === "energyBarPowerup" ||
      hudTourStepId === "energyDrink");

  const hudTourPopoverUi = useMemo(() => {
    if (!hudTourHole) {
      return { style: {}, positionClass: "hs-hud-tour-card--below" };
    }
    const vw = typeof window !== "undefined" ? window.innerWidth : 0;
    const vh = typeof window !== "undefined" ? window.innerHeight : 0;
    const edge = isMobile ? 16 : 24;
    const isBottomTour = isHudTourBottomControlStep(hudTourStepId);
    const portraitBelowAbove = hudTourCardBelow ? "hs-hud-tour-card--below" : "hs-hud-tour-card--above";

    if (isMobile && hudTourLast) {
      const cardW = Math.min(320, vw - edge * 2);
      const leftPos = Math.max(edge, Math.min((vw - cardW) / 2, vw - cardW - edge));
      return {
        style: {
          top: "max(12px, calc(12px + env(safe-area-inset-top, 0px)))",
          left: leftPos,
          width: cardW,
        },
        positionClass: "hs-hud-tour-card--anchor-top",
      };
    }

    const { top, left, width, height, targetCenterX, targetCenterY } = hudTourHole;
    const anchorX =
      typeof targetCenterX === "number" && Number.isFinite(targetCenterX)
        ? targetCenterX
        : left + width / 2;
    const anchorY =
      typeof targetCenterY === "number" && Number.isFinite(targetCenterY)
        ? targetCenterY
        : top + height / 2;

    const isLand = isMobile && vw > vh;
    if (isLand && isBottomTour) {
      const cardW = Math.min(292, vw - edge * 2);
      const estContentH = 210;
      const gap = 16;
      const canSide = left + width + gap + cardW <= vw - edge;

      if (canSide) {
        let topPos = anchorY - estContentH / 2;
        topPos = Math.max(edge + 6, Math.min(topPos, vh - estContentH - edge - 6));
        const caretTopPct = Math.max(20, Math.min(80, ((anchorY - topPos) / estContentH) * 100));
        return {
          style: {
            top: topPos,
            left: left + width + gap,
            width: cardW,
            maxHeight: `min(${Math.round(vh * 0.82)}px, 280px)`,
            overflowY: "auto",
            "--hs-tour-caret-top": `${caretTopPct}%`,
          },
          positionClass: "hs-hud-tour-card--side-right",
        };
      }

      const aboveGap = 20;
      const aboveBlockH = Math.min(248, Math.round(vh * 0.42));
      let leftPos = anchorX - cardW / 2;
      leftPos = Math.max(edge, Math.min(leftPos, vw - cardW - edge));
      let topPos = top - aboveGap - aboveBlockH;
      topPos = Math.max(edge, Math.min(topPos, vh - aboveBlockH - edge - 8));
      const caretRaw = cardW > 0 ? ((anchorX - leftPos) / cardW) * 100 : 50;
      return {
        style: {
          top: topPos,
          left: leftPos,
          width: cardW,
          maxHeight: "min(42vh, 268px)",
          overflowY: "auto",
          "--hs-tour-caret-left": `${Math.max(8, Math.min(92, caretRaw))}%`,
        },
        positionClass: "hs-hud-tour-card--above",
      };
    }

    const mobileCardCap = isMobile && isBottomTour ? 292 : 320;
    const cardW = Math.min(isMobile ? mobileCardCap : Math.min(400, 440), vw - edge * 2);

    let topPos = hudTourCardBelow ? top + height + HUD_TOUR_CARD_GAP : top - HUD_TOUR_CARD_GAP - 200;
    if (!hudTourCardBelow && isMobile && isBottomTour) {
      topPos = top - HUD_TOUR_CARD_GAP - Math.min(230, Math.round(vh * 0.36));
    }

    let leftPos = anchorX - cardW / 2;
    leftPos = Math.max(edge, Math.min(leftPos, vw - cardW - edge));
    const bottomReserve = isMobile ? 160 : 220;
    topPos = Math.max(edge, Math.min(topPos, vh - bottomReserve));
    const caretRaw = cardW > 0 ? ((anchorX - leftPos) / cardW) * 100 : 50;
    const caretPct = Math.max(5, Math.min(95, caretRaw));
    return {
      style: {
        top: topPos,
        left: leftPos,
        width: cardW,
        "--hs-tour-caret-left": `${caretPct}%`,
      },
      positionClass: portraitBelowAbove,
    };
  }, [hudTourHole, hudTourCardBelow, isMobile, hudTourLast, hudTourStepId]);

  const hudTourCardStyle = hudTourPopoverUi.style;
  const hudTourCardPositionClass = hudTourPopoverUi.positionClass;

  return (
    <div
      id="game"
      ref={gameRootRef}
      className={hudTourOpen ? "healthsprint-root healthsprint-root--hud-tour" : "healthsprint-root"}
      style={themeVars}
    >
      <style>{/* keep CSS file; this is only for any small component transitions */}</style>
      {/* meter flash */}
      {flash && (
        <div className="meter-flash" aria-hidden="true">
          {[
            { iconKey: "energy", fallback: "⚡", val: flash.energy, key: "energy" },
            { iconKey: "time", fallback: "⏱", val: flash.time, key: "time" },
            { iconKey: "heart", fallback: "❤️", val: flash.health, key: "health" },
          ].map((m) => {
            const cls = m.val === 0 ? "flash-zero" : m.val > 0 ? "flash-up" : "flash-down";
            const sign = m.val > 0 ? "+" : "";
            const src = iconUrls[m.iconKey];
            return (
              <div key={m.key} className={`flash-item ${cls}`}>
                <span className="flash-item-icon">
                  <ThemeIconImg src={src} fallback={m.fallback} />
                </span>
                {sign}
                {m.val}
              </div>
            );
          })}
        </div>
      )}


      

      {/* ── GAME SCREEN ── */}
      {!isLoading && gameDays.length > 0 && (
        <div id="game-screen" className={screen === "recovery" ? "hidden" : ""}>
          {/* HUD */}
          <div id="hud">
            <div className="meters">
              <div className="meter-icons-column" ref={tourMeterIconsGroupRef}>
                <div className="meter-icon">
                  <ThemeIconImg src={iconUrls.energy} fallback="⚡" />
                </div>
                <div className="meter-icon">
                  <ThemeIconImg src={iconUrls.time} fallback="⏱" />
                </div>
                <div className="meter-icon">
                  <ThemeIconImg src={iconUrls.heart} fallback="❤️" />
                </div>
              </div>
              <div className="meter-bars-column" ref={tourMeterBarsGroupRef}>
                <div className="meter-bar-row-align">
                  <div className="meter-bar-bg">
                    <div
                      className="meter-bar-fill energy"
                      id="energy-bar"
                      style={{ width: `${displayEnergy}%` }}
                    />
                  </div>
                </div>
                <div className="meter-bar-row-align">
                  <div className="meter-bar-bg">
                    <div className="meter-bar-fill time" id="time-bar" style={{ width: `${displayTime}%` }} />
                  </div>
                </div>
                <div className="meter-bar-row-align">
                  <div className="meter-bar-bg">
                    <div
                      className="meter-bar-fill health"
                      id="health-bar"
                      style={{ width: `${displayHealth}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="meter-vals-column">
                <div className="meter-val-row-align">
                  <div className="meter-val" id="energy-val">
                    {displayEnergy}
                  </div>
                </div>
                <div className="meter-val-row-align">
                  <div className="meter-val" id="time-val">
                    {displayTime}
                  </div>
                </div>
                <div className="meter-val-row-align">
                  <div className="meter-val" id="health-val">
                    {displayHealth}
                  </div>
                </div>
              </div>
            </div>
            <div className="day-counter" ref={tourDayCounterRef}>
              <div className="day-label">DAY</div>
              <div className="day-num">{day + 1}</div>
            </div>
          </div>

          {/* Day Tracker */}
          <div id="day-tracker">
            {dayTrackerDots.map((d) => (
              <div
                key={d}
                ref={d === day + 1 ? activeDayDotRef : undefined}
                className={[
                  "day-dot",
                  d < day + 1 ? "done" : "",
                  d === day + 1 ? "active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {d}
              </div>
            ))}
          </div>


          {/* Power-ups + sound: one column so circles share the same left edge */}
          <div id="left-control-stack">
            <div id="powerups">
              <button
                type="button"
                className={`powerup-btn ${powerups.bar <= 0 ? "disabled" : ""}`}
                id="bar-btn"
                ref={tourEnergyBarBtnRef}
                aria-label="Energy bar: +10 Energy, +5 Health. Uses one charge."
                data-powerup-hint="Energy bar — +10 Energy, +5 Health"
                onClick={() => handlePowerup("bar")}
                disabled={powerups.bar <= 0}
              >
                <ThemeIconImg src={iconUrls.cookie} fallback="🍫" />
                <div className="powerup-count" id="bar-count">
                  {powerups.bar}
                </div>
              </button>
              <button
                type="button"
                className={`powerup-btn ${powerups.drink <= 0 ? "disabled" : ""}`}
                id="drink-btn"
                ref={tourEnergyDrinkRef}
                aria-label="Energy drink: +10 Energy, −5 Time Management. Uses one charge."
                data-powerup-hint="Energy drink — +10 Energy, −5 Time"
                onClick={() => handlePowerup("drink")}
                disabled={powerups.drink <= 0}
              >
                <ThemeIconImg src={iconUrls.drink} fallback="🥤" />
                <div className="powerup-count" id="drink-count">
                  {powerups.drink}
                </div>
              </button>
            </div>

            {questionSoundEnabled && (
              <div id="sound-control-wrapper">
                <button
                  type="button"
                  id="sound-btn"
                  ref={tourSoundRef}
                  title={soundTitle}
                  onClick={() => setMuted((m) => !m)}
                  className={muted || volume === 0 ? "sound-muted" : ""}
                >
                  <ThemeIconImg src={iconUrls.sound} fallback={muted || volume === 0 ? "🔇" : "🔊"} />
                </button>
                <input
                  type="range"
                  id="volume-slider"
                  min="0"
                  max="1"
                  step="0.05"
                  value={muted ? 0 : volume}
                  onChange={(e) => {
                    setVolume(parseFloat(e.target.value));
                    if (muted) setMuted(false);
                  }}
                />
              </div>
            )}
          </div>

          {/* Main Content */}
          <div id="main-content">
            {/* Scenario View */}
            {(screen === "game" || screen === "dyk") && (
              <div id="scenario-view" className="fade-in" style={{ pointerEvents: screen === "dyk" ? "none" : "auto" }}>
                <div className="scenario-title" id="scenario-title">
                  {dayData?.title || `DAY ${day + 1}`}
                </div>

                <div className="scenario-divider">
                  <div className="sdot" />
                  <div className="sline" />
                  <div className="sdot" />
                </div>

                <div className="scenario-text" id="scenario-text">
                  {renderScenarioLines(dayData?.scenario || "")}
                </div>

                <div
                  className={`choices${
                    committingChoiceIdx !== null || impactBeforeDyk ? " choices--locking" : ""
                  }`}
                  id="choices"
                >
                  {(dayData?.choices || []).map((c, i) => {
                    const showSelected =
                      (previewIdx === i && committingChoiceIdx === null) ||
                      committingChoiceIdx === i ||
                      (impactBeforeDyk && lastChosenIdx0 === i);
                    /* Mobile: swap colors on tap (preview) immediately; only one row. Desktop: swap only during pre-commit delay. */
                    const showSwapColors =
                      committingChoiceIdx === i ||
                      (isMobile && previewIdx === i && committingChoiceIdx === null) ||
                      (impactBeforeDyk && lastChosenIdx0 === i);
                    return (
                      <button
                        key={i}
                        className={`choice-btn ${showSelected ? "selected" : ""} ${
                          showSwapColors ? "choice-committing" : ""
                        }`.trim()}
                        onClick={() => {
                          if (committingChoiceIdx !== null || impactBeforeDyk) return;
                          if (isMobile) {
                            setPreviewIdx(i);
                          } else {
                            scheduleChoiceCommit(i);
                          }
                        }}
                        onMouseEnter={() =>
                          !isMobile &&
                          committingChoiceIdx === null &&
                          !impactBeforeDyk &&
                          setPreviewIdx(i)
                        }
                        onMouseLeave={() =>
                          !isMobile &&
                          committingChoiceIdx === null &&
                          !impactBeforeDyk &&
                          setPreviewIdx(null)
                        }
                        type="button"
                        disabled={screen !== "game" || impactBeforeDyk}
                      >
                        <div className="choice-title">{c.label}</div>
                        {c.desc && <div>{c.desc}</div>}
                      </button>
                    );
                  })}
                </div>
                {isMobile && screen === "game" && (
                  <div style={{ textAlign: "center", marginTop: "24px", width: "100%" }}>
                    <button
                      className="submit-choice-btn"
                      onClick={() => {
                        if (previewIdx === null || committingChoiceIdx !== null || impactBeforeDyk)
                          return;
                        scheduleChoiceCommit(previewIdx);
                      }}
                      disabled={previewIdx === null || committingChoiceIdx !== null || impactBeforeDyk}
                    >
                      SUBMIT
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* DYK View Popup */}
            {screen === "dyk" && (
              <div className="dyk-popup-overlay">
                <div id="dyk-view" className="dyk-view fade-in-popup">
                  <div className="dyk-card">
                    <div className="dyk-title">Do You Know?</div>
                    <div className="dyk-body" id="dyk-body">
                      {dykPopupText ? <p>{dykPopupText}</p> : null}
                    </div>
                  </div>
                  <button className="continue-btn" onClick={handleContinue} type="button">
                    {day + 1 >= totalDays ? "FINISH" : "CONTINUE"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recovery Overlay */}
      {screen === "recovery" && (
        <>
          <div id="dim-overlay" className="dim-overlay" />
          <div id="recovery-overlay">
            <h3>⚠️ Critical Alert</h3>
            <p id="recovery-text">
              Your <strong className="recovery-strong">{recoveryLow.join(" & ")}</strong> meter is
              dangerously low!
              <br />
              <br />
              <strong className="recovery-wellness-label">Wellness Day:</strong> Health +10, Energy +10, Time
              −10.
              <br />
              <br />
              This helps you recover but costs work time.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="recovery-take" onClick={takeWellness} type="button">
                Take Wellness Day
              </button>
              <button className="recovery-skip" onClick={skipWellness} type="button">
                Push Through
              </button>
            </div>
          </div>
        </>
      )}

      {hudTourOpen && hudTourStripRects && hudTourHole && (
        <div className="hs-hud-tour-layer" role="dialog" aria-modal="true" aria-labelledby="hs-hud-tour-title">
          <div className="hs-hud-tour-strip hs-hud-tour-strip--top" style={hudTourStripRects.top} aria-hidden="true" />
          <div className="hs-hud-tour-strip hs-hud-tour-strip--left" style={hudTourStripRects.left} aria-hidden="true" />
          <div className="hs-hud-tour-strip hs-hud-tour-strip--right" style={hudTourStripRects.right} aria-hidden="true" />
          <div className="hs-hud-tour-strip hs-hud-tour-strip--bottom" style={hudTourStripRects.bottom} aria-hidden="true" />
          <div
            className={`hs-hud-tour-focus-ring${hudTourGroupZoom ? " hs-hud-tour-focus-ring--group" : ""}${
              hudTourRoundRingDesktop ? " hs-hud-tour-focus-ring--round" : ""
            }`}
            style={{
              top: hudTourHole.top,
              left: hudTourHole.left,
              width: hudTourHole.width,
              height: hudTourHole.height,
              borderColor: themeVars["--hs-dyk-bg"],
            }}
            aria-hidden="true"
          />
          <div className="hs-hud-tour-click-wall" aria-hidden="true" />
          <div
            className={`hs-hud-tour-card ${hudTourCardPositionClass}`}
            style={{
              ...hudTourCardStyle,
              backgroundColor: themeVars["--hs-dyk-bg"],
              color: themeVars["--hs-dyk-text"],
              "--hs-tour-joint": themeVars["--hs-dyk-bg"],
            }}
          >
            <div id="hs-hud-tour-title" className="hs-hud-tour-card-body">
              {hudTourCopy && (
                <>
                  <p className="hs-hud-tour-line">{hudTourCopy.line1}</p>
                  <p className="hs-hud-tour-line">{hudTourCopy.line2}</p>
                </>
              )}
            </div>
            <div
              className={`hs-hud-tour-actions${hudTourLast ? " hs-hud-tour-actions--last" : ""}`}
            >
              {!hudTourLast ? (
                <button
                  type="button"
                  className="hs-hud-tour-btn hs-hud-tour-btn--secondary"
                  onClick={onHudTourSkip}
                  style={{
                    borderColor: themeVars["--hs-box-bg"],
                    color: themeVars["--hs-dyk-text"],
                  }}
                >
                  Skip
                </button>
              ) : null}
              <button
                type="button"
                className="hs-hud-tour-btn hs-hud-tour-btn--primary"
                onClick={onHudTourNext}
                style={{
                  backgroundColor: themeVars["--hs-box-bg"],
                  color: themeVars["--hs-box-text"],
                }}
              >
                {hudTourLast ? "Start" : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

